import React from 'react';

import { SECTION_TYPES, sectionException, sectionTitle } from './homeSectionSummary';
import HomeTile from './HomeTile';

/**
 * The "Your items" row: one tile per work type this user holds, in a fixed
 * order.
 *
 * ONE RULE DECIDES WHETHER A TILE RENDERS: its count key must be PRESENT. That
 * is the whole rule, and the count's VALUE is not part of it.
 *
 * PRESENCE MEANS CAPABILITY, unchanged and still load-bearing. The server omits
 * the key entirely for a user who holds no capability behind it, so
 * `counts.risks === undefined` is "you hold no R.A.D.I.O. module" — there is
 * nothing to show and nothing to grey out.
 *
 * A PRESENT `0` STILL RENDERS ITS TILE. This reverses the rule that stood
 * before the redesign, which suppressed a zero tile as noise. The row is now
 * the page's primary navigation rather than a summary of what is busy: it has
 * to sit still. A tile that vanishes the moment its queue empties moves the
 * five beside it, so the tile a user reaches for every morning is somewhere
 * different on the morning they are up to date — and it takes away the one
 * honest way to say "you have none of these", which the panel's empty state
 * then says in words.
 *
 * Do not reintroduce a `> 0` test here to "tidy up" an empty tile, and do not
 * make the server omit a zero count to the same end. There is a test on exactly
 * that: HomeEntitlementTest asserts `counts.risks` arrives as `0` rather than
 * absent for a R.A.D.I.O. user with no risks.
 *
 * The exception line follows the delta discipline one level down: it renders
 * only when its own figure arrived and is non-zero, so a partial payload
 * narrows the tile rather than printing an "undefined overdue".
 */
const hasCapability = (count) => typeof count === 'number';

export default function HomeTiles({ counts = {}, deltas = {}, openType, onOpen }) {
    const types = SECTION_TYPES.filter((type) => hasCapability(counts[type]));

    if (types.length === 0) {
        return null;
    }

    return (
        <section aria-labelledby="home-tiles">
            <h2 id="home-tiles" className="tw-mb-2.5 tw-text-sm tw-font-semibold tw-text-charcoal">
                {$t('Your items')}
            </h2>

            {/* Six across on the widest screens, then three, then two — never
                one, because a single-column stack of six tiles is a list, and
                the row is meant to be read at a glance. */}
            <div className="tw-grid tw-gap-2.5 tw-grid-cols-2 md:tw-grid-cols-3 xl:tw-grid-cols-6">
                {types.map((type) => (
                    <HomeTile
                        key={type}
                        label={sectionTitle(type)}
                        value={counts[type]}
                        exceptionText={sectionException(type, deltas)}
                        selected={openType === type}
                        onClick={() => onOpen?.(type)}
                    />
                ))}
            </div>
        </section>
    );
}
